import {
  BrainCircuit,
  Image as ImageIcon,
  LoaderCircle,
  LogIn,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import type { AiContextExplanation, AiContextImage } from "../types";

type AiContextPanelProps = {
  selectedWord: string;
  explanation: AiContextExplanation | null;
  loading: boolean;
  error: string;
  image: AiContextImage | null;
  imageLoading: boolean;
  signedIn: boolean;
  onLogin: () => void;
  onRetry: () => void;
};

export function AiContextPanel({
  selectedWord,
  explanation,
  loading,
  error,
  image,
  imageLoading,
  signedIn,
  onLogin,
  onRetry,
}: AiContextPanelProps) {
  return (
    <aside className="ai-context-panel" aria-label="AI ile bağlamsal açıklama">
      <div className="ai-panel-heading">
        <div className="ai-mark"><BrainCircuit size={21} /></div>
        <div>
          <p className="eyebrow">AI İLE BAĞLAMDA KAVRA</p>
          <h2>{selectedWord || "Kaynak dilde açıklama"}</h2>
        </div>
      </div>

      {!selectedWord && (
        <p className="ai-panel-empty">
          Bir kelime seçtiğinde AI açıklaması sözlükten bağımsız olarak burada yüklenir.
        </p>
      )}

      {selectedWord && !signedIn && (
        <div className="ai-login-card">
          <p>Kaynak dilde kişisel AI açıklaması için hesabına giriş yap.</p>
          <button className="secondary-button" onClick={onLogin}>
            <LogIn size={17} /> Giriş yap
          </button>
        </div>
      )}

      {selectedWord && signedIn && loading && (
        <div className="ai-separate-loading" role="status">
          <LoaderCircle className="spin" size={23} />
          <div>
            <strong>AI ayrı olarak düşünüyor</strong>
            <p>Kelimenin bu cümledeki anlamı kendi dilinde sadeleştiriliyor…</p>
          </div>
        </div>
      )}

      {selectedWord && signedIn && error && !loading && (
        <div className="ai-error-card" role="alert">
          <p>{error}</p>
          <button className="secondary-button" onClick={onRetry}>
            <RefreshCw size={16} /> Tekrar dene
          </button>
        </div>
      )}

      {explanation && !loading && (
        <div className="ai-context-content">
          <span className="ai-language">{explanation.detectedLanguage}</span>
          <p className="ai-main-explanation">{explanation.explanation}</p>

          <section>
            <h3><Sparkles size={16} /> Bu bağlamdaki ipucu</h3>
            <p>{explanation.contextClue}</p>
          </section>

          <section>
            <h3>Benzer, sade bir örnek</h3>
            <blockquote>{explanation.simpleExample}</blockquote>
          </section>

          {imageLoading && (
            <div className="ai-image-loading" role="status">
              <LoaderCircle className="spin" size={20} />
              <span>Uygun lisanslı görsel ayrıca aranıyor…</span>
            </div>
          )}

          {image && !imageLoading && (
            <figure className="ai-context-image">
              <img src={image.url} alt={image.alt} loading="lazy" />
              <figcaption>
                <ImageIcon size={14} />
                <span>
                  <a href={image.sourceUrl} target="_blank" rel="noreferrer">
                    {image.creator}
                  </a>
                  {" · "}{image.license}
                </span>
              </figcaption>
            </figure>
          )}

          {!imageLoading && !image && explanation.imageQuery && (
            <p className="ai-image-note">Bu anlam için uygun lisanslı görsel bulunamadı.</p>
          )}

          <small className="ai-disclaimer">
            AI açıklaması sözlük kaynağından ayrıdır ve hata yapabilir.
          </small>
        </div>
      )}
    </aside>
  );
}
